import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Card, DisconnectOutlined } from "@/config/antd";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useSelector } from "react-redux";

const LocationMap = ({ location }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isOnline = useSelector((state) => state.network.isOnline);

  // Get the full address
  const fullAddress = location?.address || "";

  // Geocode the address to get coordinates
  useEffect(() => {
    const geocodeAddress = async () => {
      // Skip geocoding if offline
      if (!isOnline) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // Create a new Geocoder instance
        const geocoder = new window.google.maps.Geocoder();

        // Geocode the address
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: fullAddress }, (results, status) => {
            if (status === "OK") {
              resolve(results[0]);
            } else {
              reject(status);
            }
          });
        });

        // Set the coordinates from the result
        setCoordinates({
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
        });
      } catch (error) {
        console.error("Geocoding error:", error);
        // Set default coordinates (e.g., Lagos)
        setCoordinates({
          lat: 6.5244,
          lng: 3.3792,
        });
      } finally {
        setIsLoading(false);
      }
    };

    // Only attempt geocoding if online and Google Maps is loaded
    if (window.google && fullAddress && isOnline) {
      geocodeAddress();
    } else {
      setIsLoading(false);
    }
  }, [fullAddress, isOnline]);

  // Memoize the map center
  const center = useMemo(
    () => ({
      lat: coordinates?.lat || 6.5244, // Default to Lagos
      lng: coordinates?.lng || 3.3792,
    }),
    [coordinates]
  );

  // Map container style
  const containerStyle = {
    width: "100%",
    height: "300px",
    borderRadius: "8px",
  };

  return (
    <div className="mt-8">
      <Card
        className="w-full"
        styles={{
          body: { padding: 0 },
        }}
        bordered={false}>
        <div className="h-[300px] w-full">
          {isOnline ? (
            // Only render Google Map when online
            !isLoading &&
            window.google && (
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={15}
                options={{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}>
                {coordinates && (
                  <Marker position={coordinates} title={fullAddress} />
                )}
              </GoogleMap>
            )
          ) : (
            // Offline fallback
            <div className="h-full w-full bg-gray-100 rounded-lg flex flex-col items-center justify-center">
              <DisconnectOutlined className="text-yellow-600 text-2xl mb-2" />
              <p className="text-sm text-gray-600">
                Map unavailable while offline
              </p>
              {fullAddress && (
                <p className="text-xs text-gray-500 mt-2 px-4 text-center">
                  {fullAddress}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

LocationMap.propTypes = {
  location: PropTypes.shape({
    address: PropTypes.string,
    country: PropTypes.string,
    url: PropTypes.string,
  }),
};

export default LocationMap;
